import { useAuth } from '../contexts/AuthContext';
import { useCanWriteCompanySettings } from './useCanWriteCompanySettings';

/**
 * Which /team and employee-profile controls this user may be shown.
 *
 * One flag per API gate, not one rule for the screen — the routes in
 * users.routes.ts fall into four different sets, and a dispatcher lands on
 * different sides of them:
 *
 *   canCreate / canImport   POST /users, POST /users/import{,/confirm}
 *                           requireRole('admin') — non-strict, so an elevated
 *                           dispatcher passes. useCanWriteCompanySettings.
 *   canInvite / canManageCerts
 *                           POST /users/:id/invite, POST /team/invites/bulk,
 *                           POST|PUT|DELETE /users/:id/certifications…
 *                           requireRole('admin', 'office_staff') — non-strict,
 *                           so the elevated dispatcher passes here too.
 *   canEditProfile          PUT /users/:id — no middleware gate; the handler
 *                           compares `role === 'admin'` literally. Anyone else
 *                           may save only their OWN phone and emergency
 *                           contact, and every other field is dropped with a
 *                           200, so the full edit form is admin-only.
 *   canChangeStatus / canDelete
 *                           PATCH /users/:id/status, DELETE /users/:id
 *                           requireRole('admin', { strict: true }) — literal
 *                           admin, no dispatcher elevation.
 *
 * Separate fields even where two agree today: they are separate API gates, and
 * one moving must not silently move the other. Not `isAdminRole` anywhere — it
 * admits `owner`, which none of these lists contain.
 *
 * Hiding a control is not the enforcement; the API is. Reading the list and a
 * profile carries no role gate (GET /users, GET /users/:id), so nothing here
 * hides data.
 */
export interface TeamPermissions {
  canCreate: boolean;
  canImport: boolean;
  canInvite: boolean;
  canManageCerts: boolean;
  canEditProfile: boolean;
  canChangeStatus: boolean;
  canDelete: boolean;
}

export function useTeamPermissions(): TeamPermissions {
  const { user } = useAuth();
  const role = user?.role;
  const adminOrElevated = useCanWriteCompanySettings();
  const adminOrOffice = adminOrElevated || role === 'office_staff';
  const literalAdmin = role === 'admin';

  return {
    canCreate: adminOrElevated,
    canImport: adminOrElevated,
    canInvite: adminOrOffice,
    canManageCerts: adminOrOffice,
    canEditProfile: literalAdmin,
    canChangeStatus: literalAdmin,
    canDelete: literalAdmin,
  };
}
